'use client'
import { LineImageAlt } from '@qr-front/icons/line-image-alt'
import { fieldLabelCls } from '@qr-front/ui/components/InputField'
import { FileUploadToMediaStorage } from 'components/FileUploadToMediaStorage'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithReset } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { useFormContext } from 'react-hook-form'

import { TUploadedMedia } from '@/services'
import { maxLength } from '@/utils'

import { RhcField } from '../../components/RhcField'
import { FULL_NAME_MAX_LENGTH, TAboutFormFields } from './aboutFormModel'

// 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024

export const aboutFullNameAtom = atomWithReset('')
export const aboutAvatarImageAtom = atomWithReset<TUploadedMedia | null>(null)
export const aboutAvatarImageFileAtom = atomWithReset<File | null>(null)

export const PersonalAboutSubSection = () => {
	const t = useTranslations()

	const form = useFormContext<TAboutFormFields>()

	const fullNameField = (
		<RhcField
			type="text"
			required
			form={form}
			path="personal.fullName"
			atom={aboutFullNameAtom}
			fieldName="full_name"
			fieldGroup="personal_information"
			onChangeTransform={(event) =>
				maxLength(FULL_NAME_MAX_LENGTH)(event)
			}
			label={t(
				'generator__content_form_section__about__personal_section__full_name',
			)}
			placeholder={t(
				'generator__content_form_section__about__personal_section__full_name__placeholder',
			)}
		/>
	)

	const setAvatarImage = useSetAtom(aboutAvatarImageAtom)
	const [file, setFile] = useAtom(aboutAvatarImageFileAtom)

	const fileUploadTranslations = {
		title: t(
			'generator__content_form_section__about__personal_section__upload_image',
		),
		caption: t(
			'generator__content_form_section__about__personal_section__max_image_size',
			{ size: MAX_FILE_SIZE / 1024 / 1024 },
		),
		deleteButtonLabel: t('ui__file_upload__delete_button'),
		unsupportedFormatErrorMessage: t(
			'ui__file_upload__unsupported_format_error',
		),
		sizeTooLargeErrorMessage: t('ui__file_upload__file_size_error', {
			maxSize: MAX_FILE_SIZE / 1024 / 1024,
		}),
	}

	const cropDialogTranslations = {
		zoom: t('ui__image_crop_dialog__zoom'),
		closeButton: t('ui__popup__close_button'),
		saveButton: t('ui__image_crop_dialog__save_button'),
	}

	return (
		<div className="flex flex-col gap-4">
			{fullNameField}
			<div>
				<label className={fieldLabelCls}>
					{t(
						'generator__content_form_section__welcome_screen__image',
					)}
				</label>
				<FileUploadToMediaStorage
					uploadPurpose="content_image"
					initialFileList={file ? [file] : []}
					onUploadStart={(incoming: File) => {
						setFile(incoming)
					}}
					onUploadComplete={(
						uploaded: TUploadedMedia,
						original: File,
					) => {
						setAvatarImage(uploaded)
						setFile(original)
					}}
					onDelete={() => {
						setAvatarImage(null)
						setFile(null)
					}}
					resizeResolution={{ width: 2048, height: 2048 }}
					maxFileSize={MAX_FILE_SIZE}
					placeholderIcon={<LineImageAlt />}
					showCropDialog
					cropDialogTranslations={cropDialogTranslations}
					translations={fileUploadTranslations}
					className="mt-2"
				/>
			</div>
		</div>
	)
}
